import {
  Input,
  OnDestroy,
  AfterViewInit,
  ViewChildren,
  QueryList,
  NgZone,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  Component
} from '@angular/core';

import { Subject, merge, Observable, defer } from 'rxjs';
import { takeUntil, take, startWith, switchMap } from 'rxjs/operators';

import { FlyoutType, MenuPanelItem } from '../../domain/menu.domain';
import { MenuItemComponent } from './item/item.component';

/**
 * The Menu Flyout Component
 *
 * This is the wrapper for flyout contents
 */
@Component({
  template: ''

})
export abstract class MenuNavigationBaseComponent implements OnDestroy, AfterViewInit {

  /**
   * Prefix for flyout class
   */
  private static readonly FLYOUT_CLASS_PREFIX: string = 'td-wb-nav__';

  /**
   * Flyout type
   */
  @Input() flyoutType: FlyoutType;

  @Output() readonly selected: EventEmitter<void> = new EventEmitter<void>();

  @ViewChildren(MenuItemComponent) menuItemComponents: QueryList<MenuItemComponent>;

  private open: boolean = false;
  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly menuItemSelected: Observable<MenuPanelItem>  = defer((): Observable<MenuPanelItem> => {
    if (this.menuItemComponents !== undefined && this.menuItemComponents !== null) {
      return merge(...this.menuItemComponents.map((item: MenuItemComponent): EventEmitter<MenuPanelItem> => item.selected));
    }
    return this.ngZone.onStable
      .asObservable()
      .pipe(take(1), switchMap((): Observable<MenuPanelItem> => this.menuItemSelected));
  });

  protected constructor(
    private ngZone: NgZone,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  ngAfterViewInit(): void {
    this.menuItemComponents.changes
      .pipe(
        startWith(null as MenuItemComponent),
        takeUntil(this.destroy)
      )
      .subscribe((): void => {
        this.open = this.menuItemComponents.some((component: MenuItemComponent): boolean => {
          const item = component.menuItem;
          return item !== undefined && item.open && item.menu !== undefined && item.menu[0].item !== undefined;
        });
        this.resetItems();
      });
  }

  /**
   * This function returns flyout class
   *
   * @returns Returns the class for flyout.
   */
  getFlyoutClass(): string {
    return MenuNavigationBaseComponent.FLYOUT_CLASS_PREFIX + this.flyoutType;
  }

  close(): void {
    this.menuItemComponents.forEach((menuItemComponent: MenuItemComponent): void => {
      menuItemComponent.close();
    });
    this.open = false;
    this.changeDetectorRef.detectChanges();
  }

  isOpen(): boolean {
    return this.open;
  }

  private resetItems(): void {
    const changedOrDestroyed = merge(this.menuItemComponents.changes, this.destroy);

    this.menuItemSelected
      .pipe(
        takeUntil(changedOrDestroyed)
      )
      .subscribe((item: MenuPanelItem): void => {
        this.menuItemComponents.forEach((menuItemComponent: MenuItemComponent): void => {
          if (menuItemComponent.menuItem !== undefined &&
            menuItemComponent.menuItem.id !== item.id &&
            menuItemComponent.isOpen()) {
            menuItemComponent.close();
          }
        });
        this.open = item.open && item.menu !== undefined && item.menu[0].item !== undefined;
        this.selected.emit();
        this.changeDetectorRef.detectChanges();
      });
  }
}
